import { isStatValid } from '../../../../../Helpers/statsValidation/statsValidation'
import { EditableField, MoveDirection, StatItem } from '@/Models'

export interface EditorState {
  published: StatItem[]
  draft: StatItem[]
  editingId: string | null
  /** Items whose validation errors should now be visible. */
  validatedIds: string[]
}

export type EditorAction =
  | { type: 'loaded'; items: StatItem[] }
  | { type: 'edit_started'; id: string }
  | { type: 'edit_finished' }
  | { type: 'field_changed'; id: string; field: EditableField; value: string }
  | { type: 'field_blurred'; id: string }
  | { type: 'moved'; id: string; direction: MoveDirection }
  | { type: 'visibility_toggled'; id: string }
  | { type: 'publish_blocked' }
  | { type: 'discarded' }
  | { type: 'published'; items: StatItem[] }

export const initialEditorState: EditorState = {
  published: [],
  draft: [],
  editingId: null,
  validatedIds: [],
}

const addUnique = (ids: string[], id: string): string[] => (ids.includes(id) ? ids : [...ids, id])

function isEditingInvalid(state: EditorState): boolean {
  if (state.editingId === null) {
    return false
  }
  const item = state.draft.find((entry) => entry.id === state.editingId)
  return item ? !isStatValid(item) : false
}

export function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'loaded':
      return {
        published: action.items,
        draft: action.items,
        editingId: null,
        validatedIds: [],
      }

    case 'edit_started': {
      if (state.editingId === action.id) {
        return state
      }
      return { ...state, editingId: action.id }
    }

    case 'edit_finished': {
      if (state.editingId === null) {
        return state
      }
      if (isEditingInvalid(state)) {
        return {
          ...state,
          validatedIds: addUnique(state.validatedIds, state.editingId),
        }
      }
      return { ...state, editingId: null }
    }

    case 'field_changed':
      return {
        ...state,
        draft: state.draft.map((item) =>
          item.id === action.id ? { ...item, [action.field]: action.value } : item,
        ),
      }

    case 'field_blurred':
      return {
        ...state,
        validatedIds: addUnique(state.validatedIds, action.id),
      }

    case 'moved': {
      const from = state.draft.findIndex((item) => item.id === action.id)
      const to = action.direction === 'up' ? from - 1 : from + 1
      if (from === -1 || to < 0 || to >= state.draft.length) {
        return state
      }
      const next = [...state.draft]
      const [moved] = next.splice(from, 1)
      if (!moved) {
        return state
      }
      next.splice(to, 0, moved)
      return { ...state, draft: next }
    }

    case 'visibility_toggled':
      return {
        ...state,
        draft: state.draft.map((item) =>
          item.id === action.id ? { ...item, visible: !item.visible } : item,
        ),
      }

    case 'publish_blocked': {
      const invalid = state.draft.filter((item) => !isStatValid(item))
      const first = invalid[0]
      if (!first) {
        return state
      }
      return {
        ...state,
        editingId: first.id,
        validatedIds: invalid.reduce((ids, item) => addUnique(ids, item.id), state.validatedIds),
      }
    }

    case 'discarded':
      return {
        ...state,
        draft: state.published,
        editingId: null,
        validatedIds: [],
      }

    case 'published':
      return {
        published: action.items,
        draft: action.items,
        editingId: null,
        validatedIds: [],
      }

    default:
      return state
  }
}
